import React, { useCallback, useEffect, useRef, useState } from 'react';
import FontColorPickerModal from '../fontColorPickerModal/FontColorPickerModal';
import ColorPicker from '../colorPicker/ColorPicker';
import ColorPalette from '../colorPalette/ColorPalette';
import FormThemePublishModal from '../formThemesUnpublishModal/FormThemePublishModal';
import { getService } from '@Client/runner.hooks/useService';
import IFormThemeDetail, {
  FormThemeLogoAlignment,
  FormThemeFontColorType,
  FormThemeStatus
} from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeDetail';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import LogoPicker, { PendingLogoOperation } from '../logoPicker/LogoPicker';
import { FileFolder } from '@Shared.Angular/flowingly.services/file.service';
import { ThemeService } from '@Shared.Angular/flowingly.services/themeService';

const MAX_THEME_NAME_LENGTH = 100;
const MAX_THEME_DESCRIPTION_LENGTH = 300;
const LOGO_THEME_FIELD_KEY = 'ThemeLogo';
const HEX_COLOR_PATTERN = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
const INVALID_HEX_COLOR_MESSAGE =
  'Invalid colour code. Please enter a valid HEX value (e.g. #FFF or #FFFFFF).';

type ColorFieldName = 'formBackgroundColor' | 'primaryButtonColor';
type ColorFieldValues = Record<ColorFieldName, string>;

const normalizeHexColor = (color: string): string | null => {
  const normalizedColor = color.toUpperCase();
  if (!HEX_COLOR_PATTERN.test(normalizedColor)) {
    return null;
  }

  if (normalizedColor.length === 4) {
    return `#${normalizedColor[1]}${normalizedColor[1]}${normalizedColor[2]}${normalizedColor[2]}${normalizedColor[3]}${normalizedColor[3]}`;
  }

  return normalizedColor;
};

const formatColorInput = (color: string): string => {
  const compactColor = color.replace(/\s+/g, '');
  const colorWithHash =
    compactColor && !compactColor.startsWith('#')
      ? `#${compactColor}`
      : compactColor;

  return normalizeHexColor(colorWithHash) ?? colorWithHash.toUpperCase();
};

const isCompleteHexInput = (color: string): boolean =>
  color.length === 4 || color.length === 7;

const isCompleteHexColor = (color: string): boolean =>
  isCompleteHexInput(color) && Boolean(normalizeHexColor(color));

const preserveEquivalentColorInput = (
  currentInput: string,
  themeColor: string
): string => {
  const normalizedCurrentInput = normalizeHexColor(currentInput);
  const normalizedThemeColor = normalizeHexColor(themeColor);

  return normalizedCurrentInput &&
    normalizedCurrentInput === normalizedThemeColor
    ? currentInput
    : formatColorInput(themeColor);
};

const defaultFormState: IFormThemeDetail = {
  id: EmptyGuid,
  name: '',
  description: '',
  fontColors: [ThemeService.DEFAULT_FONT_COLOR],
  formBackgroundColor: ThemeService.DEFAULT_FORM_BG_COLOR,
  primaryButtonColor: ThemeService.DEFAULT_PRIMARY_BUTTON_COLOR,
  logoAlignment: FormThemeLogoAlignment.Center,
  logoFileId: null,
  fontColorType: FormThemeFontColorType.Default,
  isDefaultTheme: false,
  status: FormThemeStatus.Draft,
  isDraftVersionAvailable: false
};

type Props = {
  formThemeId: Guid;
  copy?: boolean;
};

export default function FormThemeEditor({ formThemeId, copy }: Props) {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const [themeNameError, setThemeNameError] = useState('');
  const [theme, setTheme] = useState<IFormThemeDetail>(defaultFormState);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [showFontModal, setShowFontModal] = useState(false);
  const [pendingLogoOperation, setPendingLogoOperation] =
    useState<PendingLogoOperation>({
      type: 'none'
    });
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [colorInputs, setColorInputs] = useState<ColorFieldValues>(() => ({
    formBackgroundColor: formatColorInput(defaultFormState.formBackgroundColor),
    primaryButtonColor: formatColorInput(defaultFormState.primaryButtonColor)
  }));
  const [colorErrors, setColorErrors] = useState<ColorFieldValues>({
    formBackgroundColor: '',
    primaryButtonColor: ''
  });
  const [invalidColorFields, setInvalidColorFields] = useState<
    Record<ColorFieldName, boolean>
  >({
    formBackgroundColor: false,
    primaryButtonColor: false
  });

  const $state = getService('$state');
  const notificationService = getService('notificationService');
  const permissionsService = getService('permissionsService');
  const formThemesApiService = getService('formThemesApiService');
  const fileService = getService('fileService');

  const hasFormThemesSaveAccess = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.FORM_THEME_CREATE
  );
  const hasFormThemesPublishAccess =
    permissionsService.currentUserHasPermission(
      FlowinglyPermissions.FORM_THEME_PUBLISH
    );

  useEffect(() => {
    (async () => {
      if (formThemeId !== EmptyGuid) {
        const formData = await formThemesApiService.getFormTheme(
          formThemeId,
          true
        );
        if (copy) {
          setTheme({
            ...formData,
            id: EmptyGuid,
            name: `Copy of ${formData.name}`,
            status: FormThemeStatus.Draft
          });
        } else {
          setTheme((prev) => ({ ...prev, ...formData }));
        }
      }
      setIsFormLoaded(true);
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (
        pendingLogoOperation.type === 'add' ||
        pendingLogoOperation.type === 'replace'
      ) {
        URL.revokeObjectURL(pendingLogoOperation.preview);
      }
    };
  }, [pendingLogoOperation]);

  useEffect(() => {
    const formBackgroundColorInput = formatColorInput(
      theme.formBackgroundColor
    );
    const primaryButtonColorInput = formatColorInput(theme.primaryButtonColor);

    setColorInputs((previousInputs) => ({
      ...previousInputs,
      formBackgroundColor: preserveEquivalentColorInput(
        previousInputs.formBackgroundColor,
        theme.formBackgroundColor
      ),
      primaryButtonColor: preserveEquivalentColorInput(
        previousInputs.primaryButtonColor,
        theme.primaryButtonColor
      )
    }));
    setColorErrors((previousErrors) => ({
      ...previousErrors,
      formBackgroundColor: '',
      primaryButtonColor: ''
    }));
    setInvalidColorFields({
      formBackgroundColor: !isCompleteHexColor(formBackgroundColorInput),
      primaryButtonColor: !isCompleteHexColor(primaryButtonColorInput)
    });
  }, [theme.formBackgroundColor, theme.primaryButtonColor]);

  const updateTheme = useCallback(
    <K extends keyof IFormThemeDetail>(key: K, value: IFormThemeDetail[K]) => {
      setTheme((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const closePublishModal = () => {
    if (isRequestPending) {
      return;
    }
    setShowPublishModal(false);
  };
  const closeUnpublishModal = () => {
    if (isRequestPending) {
      return;
    }
    setShowUnpublishModal(false);
  };

  const upsertFontColor = (colors: string[]) =>
    updateTheme('fontColors', colors);

  const handleValidColorChange = (
    fieldName: ColorFieldName,
    color: string
  ): void => {
    const normalizedColor = normalizeHexColor(color);
    if (!normalizedColor) {
      return;
    }

    updateTheme(fieldName, normalizedColor);
    setColorInputs((previousInputs) => ({
      ...previousInputs,
      [fieldName]: normalizedColor
    }));
    setColorErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: ''
    }));
    setInvalidColorFields((previousInvalidFields) => ({
      ...previousInvalidFields,
      [fieldName]: false
    }));
  };

  const onFormBackgroundColorChange = (color: string): void =>
    handleValidColorChange('formBackgroundColor', color);
  const onPrimaryButtonColorChange = (color: string): void =>
    handleValidColorChange('primaryButtonColor', color);

  const handleLogoAdd = (file: File) => {
    const preview = URL.createObjectURL(file);

    if (theme.logoFileId) {
      setPendingLogoOperation({
        type: 'replace',
        file,
        preview,
        oldFileId: theme.logoFileId
      });
    } else {
      setPendingLogoOperation({
        type: 'add',
        file,
        preview
      });
    }
  };

  useEffect(() => {
    return () => {
      if ('preview' in pendingLogoOperation && pendingLogoOperation.preview) {
        URL.revokeObjectURL(pendingLogoOperation.preview);
      }
    };
  }, [pendingLogoOperation]);

  const handleLogoRemove = () => {
    if (theme.logoFileId) {
      setPendingLogoOperation({
        type: 'remove',
        removedFileId: theme.logoFileId
      });
    } else {
      setPendingLogoOperation({
        type: 'none'
      });
    }
  };

  const validateThemeName = async (): Promise<boolean> => {
    const trimmedName = theme.name?.trim() ?? '';

    if (!trimmedName) {
      setThemeNameError('Theme name cannot be empty');
      return false;
    }
    setThemeNameError('');
    return true;
  };

  const processPendingLogoOperations = async (): Promise<Guid | null> => {
    let finalLogoFileId = theme.logoFileId;

    try {
      if (pendingLogoOperation.type === 'add') {
        const response = await fileService.uploadFileToFolder(
          pendingLogoOperation.file,
          FileFolder.FormThemes,
          LOGO_THEME_FIELD_KEY
        );
        finalLogoFileId = response.data as Guid;
      } else if (pendingLogoOperation.type === 'replace') {
        const response = await fileService.uploadFileToFolder(
          pendingLogoOperation.file,
          FileFolder.FormThemes,
          LOGO_THEME_FIELD_KEY
        );
        finalLogoFileId = response.data as Guid;

        try {
          await fileService.deleteThemeLogo(pendingLogoOperation.oldFileId);
        } catch (error) {
          console.warn('Failed to delete old logo file:', error);
        }
      } else if (pendingLogoOperation.type === 'remove') {
        await fileService.deleteThemeLogo(pendingLogoOperation.removedFileId);
        finalLogoFileId = null;
      } else {
        finalLogoFileId = theme.logoFileId;
      }

      return finalLogoFileId;
    } catch (error) {
      notificationService.showErrorToast('Failed to process logo file');
      throw error;
    }
  };

  const saveFormThemeData = async (): Promise<Guid | null> => {
    const isValid = await validateThemeName();
    if (!isValid) return null;

    const finalLogoFileId = await processPendingLogoOperations();

    const dataToSave = { ...theme, logoFileId: finalLogoFileId };

    if (theme.id === EmptyGuid) {
      // Create new theme
      const themeId = await formThemesApiService.createFormTheme(dataToSave);
      if (themeId) {
        updateTheme('id', themeId);
        updateTheme('logoFileId', finalLogoFileId);
        setPendingLogoOperation({ type: 'none' });
        $state.go(
          $state.current.name,
          { formThemeId: themeId },
          { notify: false, location: 'replace' }
        );
      }
      return themeId;
    } else {
      // Update existing theme
      await formThemesApiService.updateFormTheme(theme.id, dataToSave);
      updateTheme('logoFileId', finalLogoFileId);
      setPendingLogoOperation({ type: 'none' });
      return theme.id;
    }
  };

  const saveFormTheme = async () => {
    const themeId = await saveFormThemeData();
    if (!themeId) return;

    notificationService.showSuccessToast(
      theme.id === EmptyGuid
        ? 'Form Theme created successfully'
        : 'Form Theme updated successfully'
    );

    setTheme((prev) => ({
      ...prev,
      isDraftVersionAvailable: prev.status === FormThemeStatus.Published
    }));
  };

  const publishFormTheme = async () => {
    if (isRequestPending) {
      return;
    }

    setIsRequestPending(true);
    try {
      const themeId = await saveFormThemeData();
      if (!themeId) {
        setIsRequestPending(false);
        return;
      }

      await formThemesApiService.publishFormTheme(themeId);

      setTheme((prev) => ({
        ...prev,
        status: FormThemeStatus.Published,
        isDraftVersionAvailable: false
      }));

      notificationService.showSuccessToast('Form Theme Published');
      setShowPublishModal(false);
    } catch (error) {
      console.error('Error publishing form theme:', error);
      notificationService.showErrorToast(
        'Failed to publish form theme. Please try again.'
      );
    } finally {
      setIsRequestPending(false);
    }
  };

  const unPublishFormTheme = async () => {
    if (!theme.id || theme.id === EmptyGuid) {
      notificationService.showErrorToast('Cannot unpublish unsaved theme');
      return;
    }

    if (isRequestPending) {
      return;
    }

    setIsRequestPending(true);
    try {
      await formThemesApiService.unPublishFormTheme(theme.id);

      const updatedTheme = await formThemesApiService.getFormTheme(
        theme.id,
        true
      );
      setTheme((prev) => ({
        ...prev,
        ...updatedTheme
      }));

      notificationService.showSuccessToast('Form Theme Unpublished');
      setShowUnpublishModal(false);
    } catch (error) {
      console.error('Error unpublishing form theme:', error);
      notificationService.showErrorToast(
        'Failed to unpublish form theme. Please try again.'
      );
    } finally {
      setIsRequestPending(false);
    }
  };

  const handleRemoveFormBackgroundColor = () => {
    handleValidColorChange(
      'formBackgroundColor',
      ThemeService.DEFAULT_FORM_BG_COLOR
    );
  };

  const handleRemovePrimaryButtonColor = () => {
    handleValidColorChange(
      'primaryButtonColor',
      ThemeService.DEFAULT_PRIMARY_BUTTON_COLOR
    );
  };

  const handleColorInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: ColorFieldName
  ): void => {
    const value = e.target.value.replace(/\s+/g, '');
    const valueWithHash = value && !value.startsWith('#') ? `#${value}` : value;
    const uppercaseValue = valueWithHash.toUpperCase();

    setColorInputs((previousInputs) => ({
      ...previousInputs,
      [fieldName]: uppercaseValue
    }));
    setColorErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: ''
    }));

    if (!isCompleteHexInput(uppercaseValue)) {
      setInvalidColorFields((previousInvalidFields) => ({
        ...previousInvalidFields,
        [fieldName]: true
      }));
      return;
    }

    const normalizedColor = normalizeHexColor(uppercaseValue);
    if (!normalizedColor) {
      setInvalidColorFields((previousInvalidFields) => ({
        ...previousInvalidFields,
        [fieldName]: true
      }));
      setColorErrors((previousErrors) => ({
        ...previousErrors,
        [fieldName]: INVALID_HEX_COLOR_MESSAGE
      }));
      return;
    }

    setInvalidColorFields((previousInvalidFields) => ({
      ...previousInvalidFields,
      [fieldName]: false
    }));
    updateTheme(fieldName, normalizedColor);
  };

  const handleColorBlur = (fieldName: ColorFieldName): void => {
    const isValid = isCompleteHexColor(colorInputs[fieldName]);

    setInvalidColorFields((previousInvalidFields) => ({
      ...previousInvalidFields,
      [fieldName]: !isValid
    }));
    setColorErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: isValid ? '' : INVALID_HEX_COLOR_MESSAGE
    }));
  };

  const hasInvalidColor = Object.values(invalidColorFields).some(Boolean);

  return (
    <>
      <div className="theme-editor">
        <div className="theme-header">
          <div className="actions">
            {hasFormThemesSaveAccess && (
              <TextButton
                id="form-theme-save-button"
                text="Save"
                type="secondary"
                onClick={saveFormTheme}
                disabled={isRequestPending || hasInvalidColor}
              />
            )}
            {hasFormThemesPublishAccess && (
              <TextButton
                id="form-theme-publish-button"
                text="Publish"
                type="primary"
                onClick={() => setShowPublishModal(true)}
                disabled={isRequestPending || hasInvalidColor}
              />
            )}
            {hasFormThemesPublishAccess &&
              !theme.isDefaultTheme &&
              theme.status === FormThemeStatus.Published && (
                <TextButton
                  id="form-theme-unpublish-button"
                  text="Unpublish"
                  type="secondary"
                  onClick={() => setShowUnpublishModal(true)}
                  disabled={isRequestPending}
                />
              )}
          </div>
        </div>

        <div className="theme-card">
          {theme.isDraftVersionAvailable &&
            theme.status !== FormThemeStatus.Draft && (
              <div className="unpublished-changes-notice">
                <i className="fa-regular fa-circle-info" /> This theme has
                unpublished changes.
              </div>
            )}
          {themeNameError && (
            <div className="validation-errors">
              <p className="error-message-on-submit">{themeNameError}</p>
            </div>
          )}
          {/* Theme Name */}
          <div className="form-group">
            <label>Theme Name</label>
            <input
              ref={nameInputRef}
              type="text"
              id="form-theme-name-input"
              value={theme.name}
              maxLength={MAX_THEME_NAME_LENGTH}
              disabled={theme.isDefaultTheme}
              onChange={(e) => updateTheme('name', e.target.value)}
            />
            {nameInputRef.current === document.activeElement && (
              <div className="char-counter error-msg">
                Number of characters left:{' '}
                {MAX_THEME_NAME_LENGTH - theme.name.length}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <input
              ref={descriptionInputRef}
              type="text"
              id="form-theme-description-input"
              value={theme.description}
              maxLength={MAX_THEME_DESCRIPTION_LENGTH}
              onChange={(e) => updateTheme('description', e.target.value)}
            />
            {descriptionInputRef.current === document.activeElement && (
              <div className="char-counter error-msg">
                Number of characters left:{' '}
                {MAX_THEME_DESCRIPTION_LENGTH - theme.description.length}
              </div>
            )}
          </div>

          {/* Logo Section */}
          <div className="form-group logo-section">
            <label>Logo</label>
            {isFormLoaded && (
              <LogoPicker
                logoFileId={theme.logoFileId ?? null}
                alignment={theme.logoAlignment}
                pendingLogoOperation={pendingLogoOperation}
                onLogoAdd={handleLogoAdd}
                onLogoRemove={handleLogoRemove}
                onAlignmentChange={(alignment) =>
                  updateTheme('logoAlignment', alignment)
                }
              />
            )}
          </div>

          {/* Font Colours */}
          <div className="form-group font-section">
            <label className="label-with-tooltip">
              Font Colours
              <i
                className="fa-regular fa-circle-info"
                title="Applies to Instruction field"
              />
            </label>
            <div className="font-row">
              <select
                id="theme-editor-font-type"
                value={theme.fontColorType}
                onChange={(e) =>
                  updateTheme(
                    'fontColorType',
                    Number(e.target.value) as FormThemeFontColorType
                  )
                }
              >
                <option value={FormThemeFontColorType.Default}>Default</option>
                <option value={FormThemeFontColorType.Custom}>Custom</option>
              </select>

              <ColorPalette
                value={theme.fontColors}
                useDefaultPalette={
                  theme.fontColorType === FormThemeFontColorType.Default
                }
              />

              {theme.fontColorType === FormThemeFontColorType.Custom && (
                <IconButton
                  id="font-colors-edit-button"
                  iconClass="fa-pencil"
                  onClick={() => setShowFontModal(true)}
                  title={
                    theme.fontColors.length > 1
                      ? 'Change custom colours'
                      : 'Add custom colour'
                  }
                  border={false}
                />
              )}
            </div>
          </div>

          {/* Background Color */}
          <div className="form-group">
            <label>Form Background</label>
            <div className="color-picker">
              <div className="color-picker-controls">
                <input
                  type="text"
                  id="form-theme-background-input"
                  data-testid="form-theme-background-input"
                  value={colorInputs.formBackgroundColor}
                  onChange={(e) => handleColorInput(e, 'formBackgroundColor')}
                  onBlur={() => handleColorBlur('formBackgroundColor')}
                  maxLength={7}
                  aria-invalid={Boolean(colorErrors.formBackgroundColor)}
                  aria-describedby={
                    colorErrors.formBackgroundColor
                      ? 'form-theme-background-error'
                      : undefined
                  }
                />
                <ColorPicker
                  key="form-background-color-picker"
                  value={theme.formBackgroundColor}
                  onColorChange={onFormBackgroundColorChange}
                />
                {theme.formBackgroundColor !==
                  ThemeService.DEFAULT_FORM_BG_COLOR && (
                  <TextButton
                    id="form-background-color-reset-button"
                    text="Reset to default"
                    type="text-only"
                    onClick={handleRemoveFormBackgroundColor}
                  />
                )}
              </div>
              {colorErrors.formBackgroundColor && (
                <div
                  id="form-theme-background-error"
                  className="color-picker-error"
                  data-testid="form-theme-background-error"
                  role="alert"
                >
                  {colorErrors.formBackgroundColor}
                </div>
              )}
            </div>
          </div>

          {/* Button Colour */}
          <div className="form-group">
            <label>Button Colour</label>
            <div className="color-picker">
              <div className="color-picker-controls">
                <input
                  type="text"
                  id="form-theme-button-colour-input"
                  data-testid="form-theme-button-colour-input"
                  value={colorInputs.primaryButtonColor}
                  onChange={(e) => handleColorInput(e, 'primaryButtonColor')}
                  onBlur={() => handleColorBlur('primaryButtonColor')}
                  maxLength={7}
                  aria-invalid={Boolean(colorErrors.primaryButtonColor)}
                  aria-describedby={
                    colorErrors.primaryButtonColor
                      ? 'form-theme-button-colour-error'
                      : undefined
                  }
                />
                <ColorPicker
                  key="primary-button-color-picker"
                  value={theme.primaryButtonColor}
                  onColorChange={onPrimaryButtonColorChange}
                />
                {theme.primaryButtonColor !==
                  ThemeService.DEFAULT_PRIMARY_BUTTON_COLOR && (
                  <TextButton
                    id="primary-button-color-reset-button"
                    text="Reset to default"
                    type="text-only"
                    onClick={handleRemovePrimaryButtonColor}
                  />
                )}
              </div>
              {colorErrors.primaryButtonColor && (
                <div
                  id="form-theme-button-colour-error"
                  className="color-picker-error"
                  data-testid="form-theme-button-colour-error"
                  role="alert"
                >
                  {colorErrors.primaryButtonColor}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showFontModal && (
        <FontColorPickerModal
          onClose={() => setShowFontModal(false)}
          onActionClick={upsertFontColor}
          selectedColors={theme.fontColors}
        />
      )}
      {showPublishModal && (
        <FormThemePublishModal
          id="form-theme-publish-modal"
          closeModal={closePublishModal}
          confirmAction={publishFormTheme}
          isProcessing={isRequestPending}
          isPublish={true}
        />
      )}
      {showUnpublishModal && (
        <FormThemePublishModal
          id="form-theme-unpublish-modal"
          closeModal={closeUnpublishModal}
          confirmAction={unPublishFormTheme}
          isProcessing={isRequestPending}
          isPublish={false}
        />
      )}
    </>
  );
}
